import { CONFIDENCE_MAP_ENABLED } from '@web-platform/shared-travel-tools-constants';
import { cultureService } from '@web-platform/shared-travel-tools-data-access-backend/src/culture-service';
import { NotFoundError } from '@web-platform/shared-travel-tools-utils-backend';
import { FlightPriceMap } from '@web-platform/travel-tools-flight-price-map';
import { getTravelConfidenceMapData } from '@web-platform/travel-tools-flight-price-map-data-access-backend';
import {
  formatCityEntityId,
  isValidCityEntityId,
  toSlug,
} from '@web-platform/travel-tools-flight-price-map-utils-backend';
import { data, type LoaderFunctionArgs, type MetaFunction } from 'react-router';
import { requestContextParameters } from 'request-context-skyscanner';

import type { loader as rootLoader } from '../root';
import type { TravelConfidenceMapProps } from '@web-platform/travel-tools-flight-price-map-types/src/travel-confidence-map-props';

export const meta: MetaFunction<unknown, { root: typeof rootLoader }> = ({ matches }) => {
  const rootMatch = matches.find((m) => m.id === 'root');
  const translations = rootMatch?.data?.i18n?.translations;
  if (!translations) return [];
  return [
    { title: translations.travel_confidence_map_meta_title },
    { name: 'description', content: translations.travel_confidence_map_meta_description },
    { property: 'og:title', content: translations.travel_confidence_map_meta_title },
    {
      property: 'og:description',
      content: translations.travel_confidence_map_meta_description,
    },
  ];
};

export const loader = async ({
  context: { config, context, correlationId, logger },
  params,
}: LoaderFunctionArgs) => {
  if (!config.getBooleanValue(CONFIDENCE_MAP_ENABLED)) {
    throw new Response(null, { status: 403 });
  }

  const { cityEntityId: rawCityEntityId } = params;

  if (rawCityEntityId && !isValidCityEntityId(rawCityEntityId)) {
    throw new Response('Invalid cityEntityId', { status: 403 });
  }

  const originCityEntityId = rawCityEntityId ? formatCityEntityId(rawCityEntityId) : undefined;
  const culture = context[requestContextParameters.CULTURE];
  const jhaToken = context[requestContextParameters.JHA_TOKEN];
  const { currency, locale, market } = culture;

  const [confidenceMapData, marketInfo] = await Promise.all([
    getTravelConfidenceMapData({
      originCityEntityId,
      currency,
      locale,
      market,
      correlationId,
      logger,
      jhaToken,
    }).catch((error) => {
      if (error instanceof NotFoundError) {
        throw new Response(error.message, { status: 404 });
      }
      throw error;
    }),

    cultureService.getMarket(market, 'en-GB').catch((error) => {
      logger.warn({ message: 'Failed to fetch market info for urlCountryName', error });
      return undefined;
    }),
  ]);

  const urlCountryName = toSlug(marketInfo?.TranslatedName ?? 'country');

  const props: TravelConfidenceMapProps = {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    confidenceMapData: confidenceMapData as TravelConfidenceMapProps['confidenceMapData'],
    ...(originCityEntityId ? { originCityEntityId } : {}),
    urlCountryName,
  };

  return data(props);
};

export default function TravelFlightPriceMapPage() {
  return <FlightPriceMap />;
}
